import React, { useEffect, useState } from 'react';

/**
 * Full-screen "Preparing your workspace" loader.
 * Ported from the Career Champ design canvas (screens/workspace-loader.jsx).
 *
 * Props:
 *  - firstName:   user's first name for the greeting (defaults to "there")
 *  - activeLabel: label for the in-progress step (e.g. "Saving your profile")
 *  - onSkip:      called when the user clicks "Skip for now"
 *  - skipDelayMs: how long before the skip escape-hatch appears (default 30s)
 */
const WorkspaceLoader = ({
    firstName = 'there',
    activeLabel = 'Saving your profile',
    onSkip,
    skipDelayMs = 30000,
}) => {
    const [showSkip, setShowSkip] = useState(false);

    // Reveal the "Taking longer than expected?" escape hatch after the delay.
    useEffect(() => {
        if (!onSkip) return undefined;
        const timer = setTimeout(() => setShowSkip(true), skipDelayMs);
        return () => clearTimeout(timer);
    }, [onSkip, skipDelayMs]);

    const steps = [
        { t: 'Account created', s: 'done' },
        { t: activeLabel, s: 'active' },
        { t: 'Scoring 50 matched jobs', s: 'todo' },
        { t: 'Building your dashboard', s: 'todo' },
    ];

    return (
        <div
            className="fixed inset-0 z-[290] flex flex-col items-center justify-center overflow-hidden font-dmsans"
            style={{ background: 'radial-gradient(120% 90% at 50% 0%, #EEF1FE 0%, #F6F7FB 55%, #FFFFFF 100%)' }}
            role="status"
            aria-live="polite"
        >
            {/* Ambient blobs */}
            <div aria-hidden="true" style={{ position: 'absolute', top: -80, left: -60, width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(63,87,224,.16), transparent 70%)' }} />
            <div aria-hidden="true" style={{ position: 'absolute', bottom: -100, right: -60, width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle, rgba(139,92,246,.14), transparent 70%)' }} />

            {/* Brand */}
            <div style={{ position: 'absolute', top: 32, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 9, background: 'linear-gradient(135deg,#3F57E0,#8B5CF6)', display: 'grid', placeItems: 'center', color: '#fff', fontSize: 14, fontWeight: 800, letterSpacing: '-0.02em' }}>CC</div>
                    <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.015em', color: '#0F172A' }}>Career Champ</div>
                </div>
            </div>

            {/* Card */}
            <div
                style={{
                    width: 440, maxWidth: '90%',
                    background: '#fff', borderRadius: 22,
                    border: '1px solid #E6E8EE',
                    boxShadow: '0 24px 70px rgba(15,23,42,.14)',
                    padding: '40px 36px 32px', textAlign: 'center',
                    position: 'relative', zIndex: 1,
                }}
            >
                {/* Animated ring + sparkle */}
                <div style={{ width: 96, height: 96, margin: '0 auto 22px', position: 'relative' }}>
                    <svg className="animate-spin" viewBox="0 0 96 96" width="96" height="96">
                        <circle cx="48" cy="48" r="42" fill="none" stroke="#EEF1F5" strokeWidth="7" />
                        <circle cx="48" cy="48" r="42" fill="none" stroke="url(#wl-g)" strokeWidth="7" strokeLinecap="round" strokeDasharray="264" strokeDashoffset="92" />
                        <defs>
                            <linearGradient id="wl-g" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0" stopColor="#3F57E0" />
                                <stop offset="1" stopColor="#8B5CF6" />
                            </linearGradient>
                        </defs>
                    </svg>
                    <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
                        <div style={{ width: 54, height: 54, borderRadius: 16, background: 'linear-gradient(135deg,#3F57E0,#8B5CF6)', display: 'grid', placeItems: 'center', boxShadow: '0 8px 20px rgba(63,87,224,.35)' }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="M12 3l1.6 4.6L18 9.2l-4.4 1.6L12 15l-1.6-4.2L6 9.2l4.4-1.6L12 3z" fill="#fff" />
                                <path d="M18.5 14.5l.7 1.9 1.9.7-1.9.7-.7 1.9-.7-1.9-1.9-.7 1.9-.7.7-1.9z" fill="#fff" opacity="0.85" />
                            </svg>
                        </div>
                    </div>
                </div>

                <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', color: '#0F172A' }}>Preparing your workspace</div>
                <div style={{ fontSize: 13.5, color: '#64748B', marginTop: 8, maxWidth: 320, marginInline: 'auto', lineHeight: 1.5 }}>
                    Hang tight, {firstName} — we're saving your profile and lining up your first matches. This takes about 15 seconds.
                </div>

                {/* Progress */}
                <div style={{ marginTop: 22 }}>
                    <div style={{ height: 7, borderRadius: 999, background: '#EEF1F5', overflow: 'hidden' }}>
                        <span style={{ display: 'block', height: '100%', width: '42%', borderRadius: 999, background: 'linear-gradient(90deg,#3F57E0,#8B5CF6)' }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 7 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#3F57E0' }}>Step 2 of 4</span>
                        <span style={{ fontSize: 11, color: '#94A3B8' }}>42%</span>
                    </div>
                </div>

                {/* Steps */}
                <div style={{ marginTop: 20, textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {steps.map((st, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 10px', borderRadius: 10, background: st.s === 'active' ? '#F4F6FF' : 'transparent' }}>
                            <div style={{ width: 22, height: 22, borderRadius: 9999, flexShrink: 0, display: 'grid', placeItems: 'center', background: st.s === 'done' ? '#10B981' : st.s === 'active' ? '#3F57E0' : '#F1F3F7', border: st.s === 'todo' ? '1.5px solid #E6E8EE' : 'none' }}>
                                {st.s === 'done' ? (
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <path d="M20 6L9 17l-5-5" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                ) : st.s === 'active' ? (
                                    <svg className="animate-spin" width="13" height="13" viewBox="0 0 24 24" aria-hidden="true">
                                        <circle cx="12" cy="12" r="9" fill="none" stroke="rgba(255,255,255,.4)" strokeWidth="3" />
                                        <path d="M12 3a9 9 0 0 1 9 9" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
                                    </svg>
                                ) : (
                                    <span style={{ width: 6, height: 6, borderRadius: 9999, background: '#CBD5E1' }} />
                                )}
                            </div>
                            <span style={{ fontSize: 13, fontWeight: st.s === 'active' ? 700 : 600, color: st.s === 'todo' ? '#94A3B8' : '#0F172A' }}>{st.t}</span>
                            {st.s === 'active' && <span style={{ marginLeft: 'auto', fontSize: 11, color: '#3F57E0', fontWeight: 600 }}>Working…</span>}
                            {st.s === 'done' && <span style={{ marginLeft: 'auto', fontSize: 11, color: '#047857', fontWeight: 600 }}>Done</span>}
                        </div>
                    ))}
                </div>
            </div>

            {/* Rotating tip */}
            <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 999, background: '#fff', border: '1px solid #E6E8EE', boxShadow: '0 1px 2px rgba(15,23,42,.06)', zIndex: 1 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" fill="#F59E0B" />
                </svg>
                <span style={{ fontSize: 12, color: '#334155' }}><b style={{ color: '#0F172A' }}>Tip:</b> Connect LinkedIn to unlock warm intros from your 2nd-degree network.</span>
            </div>

            {/* Escape hatch — appears after skipDelayMs */}
            {showSkip && onSkip && (
                <div style={{ position: 'absolute', bottom: 24, left: 0, right: 0, textAlign: 'center' }}>
                    <span style={{ fontSize: 11, color: '#94A3B8' }}>
                        Taking longer than expected?{' '}
                        <button
                            type="button"
                            onClick={onSkip}
                            style={{ color: '#3F57E0', fontWeight: 600, background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}
                        >
                            Skip for now →
                        </button>
                    </span>
                </div>
            )}
        </div>
    );
};

export default WorkspaceLoader;
